import { RootState, useAppSelector, useTranslation } from "@next-platform/client-common-core";
import { Button, ButtonVariant, IconVariants, Text, TextVariants } from "@next-platform/client-common-spicy-ui";
import { useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";

import useLocalizedLocation from "@/hooks/useLocalizedLocation";

import { buildChatBackGuardState, isChatBackGuardEntry } from "./chatBackGuard";

interface INavigateBackButtonV2Props {
  isChangePersona?: boolean;
  setIsChangePersona?: (value: boolean) => void;
  path?: string;
  withLabel?: boolean;
  text?: string;
}

const NavigateBackButtonV2 = ({ isChangePersona, setIsChangePersona, path, withLabel = true, text }: INavigateBackButtonV2Props) => {
  const { t } = useTranslation();
  const entryLocation = useAppSelector((state: RootState) => state.navigation.entryLocation);

  const { pagePath } = useLocalizedLocation();
  const navigate = useNavigate();
  const isChatPage = pagePath?.split("/")[1] === "chat";

  const handleBackClick = useCallback(() => {
    if (isChangePersona && setIsChangePersona) {
      setIsChangePersona(false);
    } else {
      if (path) {
        navigate(path);
      } else {
        navigate(-1);
      }
    }
  }, [isChangePersona, navigate, path, setIsChangePersona]);

  useEffect(() => {
    if (isChatPage && entryLocation === pagePath) {
      const handlePopState = () => {
        // Still on the guard entry: something above it was popped, not the chat itself.
        if (isChatBackGuardEntry(window.history.state)) {
          return;
        }
        navigate("/");
      };

      // Already on the guard, after a reload or a return to it: arm the listener only, or a second guard would
      // make the first Back do nothing.
      if (!isChatBackGuardEntry(window.history.state)) {
        window.history.pushState(buildChatBackGuardState(window.history.state), "", window.location.href);
      }

      window.addEventListener("popstate", handlePopState);
      return () => {
        window.removeEventListener("popstate", handlePopState);
      };
    }
  }, [entryLocation, pagePath, isChatPage, navigate]);

  return (
    <div className="flex flex-row items-center">
      <Button variant={ButtonVariant.TRANSPARENT} iconStart={IconVariants.ARROW_LEFT} onClick={handleBackClick} className="!p-0 text-gray-11 h-6">
        {!!withLabel && (
          <Text as="span" variant={TextVariants.LABEL_LARGE_MEDIUM}>
            {text || t("common:back")}
          </Text>
        )}
      </Button>
    </div>
  );
};

export default NavigateBackButtonV2;
